import { CitationEditDialog } from "@/components/app/editor/citations/CitationEditDialog";
import { CitationItem } from "@/components/app/editor/citations/CitationItem";
import { editorClient } from "@/lib/editorClient";
import { Trans } from "@lingui/react/macro";
import { Button, CitationList } from "@op/sf-editor";
import { FeatherPlus, FeatherUpload } from "@subframe/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CitationImportDialog } from "@/components/app/editor/citations/CitationImportDialog";
import { useEnable } from "@/hooks/useEnable";
import { usePublished } from "@op/utils";
import { redirect } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/citations/"
)({
  component: RouteComponent,
});

interface Citation {
  id: string;
  index: number;
  author: string;
  content: string;
  source: string | null;
}

function RouteComponent() {
  const { versionId, organizationSlug, projectSlug } = Route.useParams();

  const [citations, setCitations] = useState<Citation[]>([]);

  const [citationEditDialogId, setCitationEditDialogId] = useState<
    string | undefined | null
  >(null);
  const [importDialogOpen, setImportDialogOpen] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const { data } = useQuery(
    {
      queryKey: ["editor-version-citations", { versionId }],
      queryFn: async () => {
        const res = await editorClient.versions[":versionId"].citations.$get({
          param: {
            versionId,
          },
        });
        if (!res.ok) {
          if (res.status === 401) throw redirect({ to: "/login" });
          throw new Error("Failed to get citations");
        }
        const data = await res.json();
        return data;
      },
    },
    queryClient
  );

  const canCreate = useEnable(organizationSlug, projectSlug, [
    "admin",
    "publisher",
    "editor",
  ]);

  const canImport = useEnable(organizationSlug, projectSlug, [
    "admin",
    "publisher",
  ]);

  const published = usePublished();

  const importDisabled = useMemo(
    () => !canImport || published,
    [canImport, published]
  );
  const createDisabled = useMemo(
    () => !canCreate || published,
    [canCreate, published]
  );

  useEffect(() => {
    setCitations(data ?? []);
  }, [data]);

  useEffect(() => {
    if (citationEditDialogId !== null) return;
    setTimeout(() => {
      document.body.style.pointerEvents = "auto";
    }, 1);
  }, [citationEditDialogId]);

  useEffect(() => {
    if (importDialogOpen) return;
    setTimeout(() => {
      document.body.style.pointerEvents = "auto";
    }, 1);
  }, [importDialogOpen]);

  const initialCitation = useMemo(
    () => citations.find((citation) => citation.id === citationEditDialogId),
    [citations, citationEditDialogId]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const mutateSort = useMutation({
    mutationFn: async (params: { id: string; index: number }[]) => {
      const res = await editorClient.versions[":versionId"].citations.sort.$put(
        {
          param: {
            versionId,
          },
          json: params,
        }
      );
      if (!res.ok) {
        throw new Error("Failed to sort citations");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["editor-version-citations", { versionId }],
      });
      queryClient.invalidateQueries({
        queryKey: ["editor-layout", { versionId }],
      });
    },
  });

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      if (!citations) return;
      const { active, over } = event;
      if (!over) return;

      if (active.id !== over.id) {
        const oldIndex = citations.findIndex(
          (citation) => citation.id === active.id
        );
        const newIndex = citations.findIndex(
          (citation) => citation.id === over.id
        );
        const newItems = arrayMove(citations, oldIndex, newIndex).map(
          (citation, idx) => ({ ...citation, index: idx + 1 })
        );
        setCitations(newItems);
        const newItemMap = newItems.map((citation, idx) => ({
          id: citation.id,
          index: idx + 1,
        }));
        mutateSort.mutate(newItemMap);
      }
    },
    [citations]
  );

  return (
    <div className="flex w-full flex-col items-start gap-6 mx-auto max-w-[1280px] px-4">
      <div className="w-full flex flex-row justify-end">
        <div className="flex flex-row gap-2 items-center">
          <Button
            variant="neutral-secondary"
            size="small"
            icon={<FeatherUpload />}
            disabled={importDisabled}
            onClick={() => setImportDialogOpen(true)}
          >
            <Trans>Import CSV</Trans>
          </Button>
          <Button
            variant="brand-secondary"
            size="small"
            icon={<FeatherPlus />}
            disabled={createDisabled}
            onClick={() => setCitationEditDialogId(undefined)}
          >
            <Trans>Add Citation</Trans>
          </Button>
        </div>
      </div>
      <CitationList>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={citations}
            strategy={verticalListSortingStrategy}
          >
            {citations.map((citation) => {
              return (
                <CitationItem
                  key={citation.id}
                  id={citation.id}
                  author={citation.author}
                  index={citation.index}
                  content={citation.content}
                  source={citation.source}
                  organizationSlug={organizationSlug}
                  projectSlug={projectSlug}
                  onEdit={setCitationEditDialogId}
                  versionId={versionId}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </CitationList>
      <CitationEditDialog
        citationId={citationEditDialogId}
        author={initialCitation?.author}
        content={initialCitation?.content}
        source={initialCitation?.source ?? undefined}
        organizationSlug={organizationSlug}
        projectSlug={projectSlug}
        versionId={versionId}
        onOpenChange={setCitationEditDialogId}
      />
      <CitationImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        versionId={versionId}
      />
    </div>
  );
}
